<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import {
  isWorkspaceV2Path,
  workspaceV2Path,
} from 'dashboard/helper/workspaceV2';

export default {
  name: 'KanbanTasksTab',
  props: { item: { type: Object, required: true } },
  computed: {
    ...mapGetters({
      allTasks: 'tasks/getTasks',
      uiFlags: 'tasks/getTasksUIFlags',
    }),
    tasks() {
      return (this.allTasks || []).filter(
        t => t.kanban_item_id === this.item.id
      );
    },
    isFetching() {
      return this.uiFlags && this.uiFlags.isFetching;
    },
    accountId() {
      return this.$route.params.accountId;
    },
    tasksPath() {
      const path = `/app/accounts/${this.accountId}/tasks`;
      return isWorkspaceV2Path(this.$route.path) ? workspaceV2Path(path) : path;
    },
  },
  watch: {
    'item.id'() {
      this.fetchTasks();
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.fetchTasks();
  },
  methods: {
    fetchTasks() {
      if (!this.item || !this.item.id) return;
      this.$store.dispatch('tasks/fetch', { kanban_item_id: this.item.id });
    },
    openCreate() {
      const path = `/app/accounts/${this.accountId}/tasks`;
      const query = {
        new: '1',
        kanban_item_id: String(this.item.id),
      };
      const conversationDisplayId =
        this.item.conversation_display_id ||
        this.item.conversation?.display_id ||
        this.item.conversation?.id;
      const contactId =
        this.item.contact_id ||
        this.item.contact?.id ||
        this.item.conversation?.contact?.id;
      const contactName =
        this.item.contact?.name ||
        this.item.contact?.phone_number ||
        this.item.conversation?.contact?.name ||
        this.item.conversation?.contact?.phone_number;
      if (conversationDisplayId)
        query.conversation_display_id = String(conversationDisplayId);
      if (contactId) query.contact_id = String(contactId);
      if (contactName) query.contact_name = contactName;
      this.$router.push({
        path: isWorkspaceV2Path(this.$route.path)
          ? workspaceV2Path(path)
          : path,
        query,
      });
    },
    openEdit(t) {
      const path = `/app/accounts/${this.accountId}/tasks`;
      this.$router.push({
        path: isWorkspaceV2Path(this.$route.path)
          ? workspaceV2Path(path)
          : path,
        query: { open: String(t.id) },
      });
    },
    async toggleComplete(t) {
      try {
        if (this.isDone(t)) await this.$store.dispatch('tasks/reopen', t.id);
        else await this.$store.dispatch('tasks/complete', t.id);
        this.fetchTasks();
      } catch (e) {
        useAlert('Erro');
      }
    },
    isDone(t) {
      return t.status && t.status.is_terminal;
    },
    statusStyle(t) {
      const c = (t.status && t.status.color) || '#6b7280';
      return { backgroundColor: c + '22', color: c };
    },
    statusLabel(t) {
      return (t.status && t.status.label) || t.status_key;
    },
    priorityColor(p) {
      const map = {
        urgent: 'bg-red-500',
        high: 'bg-orange-400',
        medium: 'bg-blue-400',
        low: 'bg-n-alpha-3',
        none: 'bg-n-alpha-2',
      };
      return map[p] || 'bg-n-alpha-2';
    },
    formatDate(iso) {
      try {
        const d = new Date(iso);
        const pad = n => String(n).padStart(2, '0');
        const today = new Date();
        const sameDay =
          d.getFullYear() === today.getFullYear() &&
          d.getMonth() === today.getMonth() &&
          d.getDate() === today.getDate();
        if (sameDay)
          return 'Hoje ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
        return (
          pad(d.getDate()) +
          '/' +
          pad(d.getMonth() + 1) +
          ' ' +
          pad(d.getHours()) +
          ':' +
          pad(d.getMinutes())
        );
      } catch (e) {
        return iso;
      }
    },
  },
};
</script>

<template>
  <div class="kanban-tasks-tab">
    <div class="flex items-center justify-between mb-3 px-1">
      <div class="flex items-center gap-2">
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ $t('KANBAN.TASKS_TAB.TITLE') }}
        </h3>
        <span v-if="tasks.length > 0" class="text-xs text-n-slate-11">{{
          tasks.length
        }}</span>
      </div>
      <div class="flex items-center gap-2">
        <button
          class="text-xs px-2 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white"
          @click="openCreate"
        >
          {{ $t('KANBAN.TASKS_TAB.NEW') }}
        </button>
        <router-link
          :to="tasksPath"
          class="text-xs text-n-slate-11 hover:text-blue-600"
        >
          {{ $t('KANBAN.TASKS_TAB.VIEW_ALL') }}
        </router-link>
      </div>
    </div>

    <div
      v-if="isFetching && tasks.length === 0"
      class="text-center text-n-slate-11 py-4 text-xs"
    >
      {{ $t('KANBAN.TASKS_TAB.LOADING') }}
    </div>
    <div
      v-else-if="tasks.length === 0"
      class="text-center text-n-slate-10 py-8 text-xs"
    >
      <p>{{ $t('KANBAN.TASKS_TAB.EMPTY') }}</p>
      <button class="mt-2 text-blue-600 hover:underline" @click="openCreate">
        {{ $t('KANBAN.TASKS_TAB.CREATE_FIRST') }}
      </button>
    </div>
    <ul v-else class="space-y-1.5">
      <li
        v-for="t in tasks"
        :key="t.id"
        class="flex items-start gap-2 rounded-md border border-n-weak hover:bg-n-alpha-2/50"
      >
        <input
          type="checkbox"
          :checked="isDone(t)"
          :aria-label="t.title"
          class="mt-2 ml-2 size-3.5 rounded cursor-pointer"
          @click.stop="toggleComplete(t)"
        />
        <button
          type="button"
          class="flex min-w-0 flex-1 items-start gap-2 p-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-woot-500/35"
          :aria-label="t.title"
          @click="openEdit(t)"
        >
          <span class="min-w-0 flex-1">
            <span class="flex items-center gap-1.5">
              <span
                class="size-1.5 rounded-full inline-block shrink-0"
                :class="[priorityColor(t.priority)]"
              />
              <span
                class="text-xs font-medium truncate"
                :class="[
                  isDone(t)
                    ? 'line-through text-n-slate-10'
                    : 'text-n-slate-12',
                ]"
              >
                {{ t.title }}
              </span>
            </span>
            <span
              class="flex items-center gap-2 mt-0.5 text-[10px] text-n-slate-11"
            >
              <span
                v-if="t.due_at"
                :class="t.overdue ? 'text-red-500 font-medium' : ''"
              >
                {{ formatDate(t.due_at) }}
              </span>
              <span v-if="t.assigned_to">{{ t.assigned_to.name }}</span>
            </span>
          </span>
          <span
            class="text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap"
            :style="statusStyle(t)"
          >
            {{ statusLabel(t) }}
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>
